import { useMemo } from 'react'
import clsx from 'clsx'
import { Descendant } from 'slate'

import { TableColumnListValue, TypographyGroupValue } from '@makyoapp/makyo-common'
import {
  cn,
  ColorVariant,
  CornerRadius,
  getTypographyTokenStyle,
  HoverEffectVariant,
  HoverIntensity,
  MaterialIcon,
  Rating,
  RawSpacingInput,
  SingleRow,
  SingleRowProps,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { MarkdownPreview } from '@/lib/markdown'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompSingleRowData } from '@/hooks/__project__/useCompSingleRowData'
import { ComponentSettingKey } from '@/shared/builder'

import { CompHeadline } from '../../shared/CompHeadline'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'

export interface CompSingleRowWrapperProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

/**
 * CompSingleRowWrapperCore
 */
export function CompSingleRowWrapperCore({
  infos: { component, settings, containerClient, rowId, detailTableId, detailRowIndex, currentParentBg },
  tableData: { dbTable: table },
}: CompSingleRowWrapperProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================

  const dataSource = settings[ComponentSettingKey.DataListData] as TableColumnListValue

  const [parsedDataList, isLoading] = useCompSingleRowData({
    componentId: component._id,
    table,
    dataSource,
    rowId,
  })

  // ========================= design =========================
  const backgroundColor = currentParentBg

  // base
  const width = settings[ComponentSettingKey.DataListDesignWidth]?.[0] ?? 100
  const align = settings[ComponentSettingKey.DataListDesignHAlign] ?? 'center'
  // const padding = settings?.[ComponentSettingKey.DataListDesignPadding] as RawSpacingInput
  const margin = settings?.[ComponentSettingKey.DataListDesignMargin] as RawSpacingInput

  // design
  const variant = settings?.[ComponentSettingKey.DataListDesignVariant] as SingleRowProps['styleVariant']
  const border = settings?.[ComponentSettingKey.DataListDesignBorderVariant] as string
  const shape = settings?.[ComponentSettingKey.DataListDesignRoundedCorner] as CornerRadius
  const titleEndAligned = (settings?.[ComponentSettingKey.DataListDesignTitleEnd] as boolean) ?? false
  const contentEndAligned = (settings?.[ComponentSettingKey.DataListDesignContentEnd] as boolean) ?? false
  const oddColor = settings?.[ComponentSettingKey.DataListDesignColor1] || 'primary-80'
  const evenColor = settings?.[ComponentSettingKey.DataListDesignColor2] || 'primary-80'

  // const hoverEffect = settings?.[ComponentSettingKey.DataListDesignHoverEffect] as HoverEffectVariant
  // const hoverIntensity = 'md' as HoverIntensity

  // typography
  const typoSetting = settings?.[ComponentSettingKey.DataListDesignTypography] as TypographyGroupValue
  const contentTypo = typoSetting?.[ComponentSettingKey.DataListDesignContentTypography]

  // ========================= component title / headline =========================
  const headline = (settings?.[ComponentSettingKey.DataListHeadline] as Descendant[]) ?? []
  const headlineAlign = settings[ComponentSettingKey.DataListDesignHeadlineAlign] ?? 'start'
  const headlineMargin = settings?.[ComponentSettingKey.DataListDesignHeadlineMargin]
  const headlineTypo = typoSetting?.[ComponentSettingKey.DataListDesignTitleTypography]

  // ========================= extra =========================

  const typographyStyles = getTypographyTokenStyle(contentTypo)

  const contentTransparentColor = useMemo(() => {
    return variant === 'stripped-title' || variant === 'title-bg' ? `var(--color-on-${currentParentBg})` : 'inherit'
  }, [variant, currentParentBg])

  // ========================= render =========================

  // todo: loading state

  const shouldShow = !!(parsedDataList || []).length
  if (!shouldShow) return null

  const data = parsedDataList.map((pds) => ({
    label: pds.label,
    value: (
      <div style={{ ...typographyStyles, color: contentTransparentColor }}>
        {pds.isRating && (
          <span className={clsx('flex items-center h-full', !contentEndAligned ? 'justify-start' : 'justify-end')}>
            <Rating
              backgroundColor={backgroundColor}
              readOnly
              count={pds.ratingSettings?.ratingMax as number}
              value={Number(pds.value)}
            />
          </span>
        )}
        {pds.isIcon && (
          <span className={clsx('flex', !contentEndAligned ? 'justify-start' : 'justify-end')}>
            <MaterialIcon icon={pds.value ?? ''} />
          </span>
        )}
        {pds.isImage && (
          <span className={clsx('flex', !contentEndAligned ? 'justify-start' : 'justify-end')}>
            <img className={clsx('shrink-0 object-cover w-10 h-10 rounded-md')} src={pds.value} />
          </span>
        )}
        {pds.isImageArray && (
          <span className={clsx('flex gap-2', !contentEndAligned ? 'justify-start' : 'justify-end')}>
            {(() => {
              try {
                const imageArray = typeof pds.value === 'string' ? JSON.parse(pds.value) : pds.value
                if (!Array.isArray(imageArray)) return null

                const maxVisible = 4
                const visibleImages = imageArray.slice(0, maxVisible)
                const remainingCount = imageArray.length - maxVisible

                return (
                  <>
                    {visibleImages.map((image: any, index: number) => (
                      <img
                        key={image.Url || index}
                        className='shrink-0 object-cover w-10 h-10 rounded-md'
                        src={image.Url}
                        alt={image.Title || `Image ${index + 1}`}
                      />
                    ))}
                    {remainingCount > 0 && (
                      <div className='relative shrink-0 w-10 h-10 rounded-md overflow-hidden'>
                        <img
                          className='object-cover w-full h-full opacity-50'
                          src={imageArray[maxVisible].Url}
                          alt={imageArray[maxVisible].Title || `Image ${maxVisible + 1}`}
                        />
                        <div className='absolute inset-0 bg-black/80 flex items-center justify-center'>
                          <span className='text-white text-xs font-medium'>+{remainingCount}</span>
                        </div>
                      </div>
                    )}
                  </>
                )
              } catch (error) {
                console.error('Error parsing image array:', error)
                return null
              }
            })()}
          </span>
        )}
        {pds.isUrl && (
          <span>
            <a href={pds.value} target='_blank' rel='noreferrer'>
              {pds.value}
            </a>
          </span>
        )}
        {(pds.isQrCode || pds.isBarcode) && (
          <span className={clsx('flex', !contentEndAligned ? 'justify-start' : 'justify-end')}>
            <img
              className={cn('h-10 object-contain', pds.isBarcode ? 'w-auto' : 'w-10')}
              src={pds.value}
              alt='QR Code'
            />
          </span>
        )}
        {!pds.isUrl &&
          !pds.isImage &&
          !pds.isIcon &&
          !pds.isRating &&
          !pds.isQrCode &&
          !pds.isImageArray &&
          !pds.isBarcode && (
            <MarkdownPreview
              components={{
                p: (props) => (
                  <p
                    {...props}
                    style={{
                      ...typographyStyles,
                    }}
                  />
                ),
              }}
              style={{
                backgroundColor: 'transparent',
                boxSizing: 'border-box',
                height: '100%',
                margin: '0px',
                textAlign: !contentEndAligned ? 'start' : 'end',
                color: contentTransparentColor,
                font: typographyStyles.font,
                letterSpacing: typographyStyles.letterSpacing,
                justifyContent: !contentEndAligned ? 'flex-start' : 'flex-end',
                ...typographyStyles,
              }}
              source={pds.value}
            />
          )}
      </div>
    ),
  }))

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const singleRowComponent = (
    <SingleRow
      data={data}
      alignComponent={align}
      alignTitlesToEnd={titleEndAligned}
      alignContentToEnd={contentEndAligned}
      borderVariant={border as 'all' | 'row' | 'none'}
      color1={oddColor as ColorVariant}
      color2={evenColor as ColorVariant}
      cornerRadius={shape}
      width={width}
      margin={margin}
      styleVariant={variant}
      tableContentTypography={contentTypo}
      title={
        <CompHeadline
          containerClient={containerClient}
          detailTableId={detailTableId}
          detailRowIndex={detailRowIndex}
          headline={headline}
          headlineMargin={headlineMargin}
          headlineTypo={headlineTypo}
          compTitleAlign={headlineAlign}
          backgroundColor={backgroundColor}
          colorType='adaptive'
        />
      }
      backgroundColor={backgroundColor}
      // hoverEffect={hoverEffect as HoverEffectVariant}
      // hoverIntensity={hoverIntensity as HoverIntensity}
    />
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {singleRowComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return singleRowComponent
}
